import { type FormEvent, useEffect, useMemo, useState } from "react";

import FinanceDateFilter from "../../components/finance/FinanceDateFilter";
import FinanceFilterField from "../../components/finance/FinanceFilterField";
import FinanceMultiSelect from "../../components/finance/FinanceMultiSelect";
import FinanceFilterPanel from "../../components/finance/FinanceFilterPanel";
import FinanceHelpLabel from "../../components/finance/FinanceHelpLabel";
import FinanceIncompleteBadge from "../../components/finance/FinanceIncompleteBadge";
import FinanceMetricCard from "../../components/finance/FinanceMetricCard";
import FinancePagination from "../../components/finance/FinancePagination";
import FinanceWarningBanner from "../../components/finance/FinanceWarningBanner";
import TableLoadingSkeleton from "../../components/shared/TableLoadingSkeleton";
import CopyableIdentifier from "../../components/controls/CopyableIdentifier";
import { fetchFinanceSales } from "../../finance/sales-api";
import {
  financeRangeFromSearch,
  type FinanceDateRangeValue,
} from "../../finance/dates";
import {
  formatMoney,
  formatPercent,
  knownMoney,
  previousPeriodComparison,
} from "../../finance/formatting";
import type {
  FinanceSalesCurrencySummary,
  FinanceSalesResponse,
  FinanceSalesCategory,
  FinanceSalesClass,
  FinanceSalesRow,
} from "../../finance/sales-types";

type SortField = "gross_profit" | "net_sales" | "gross_margin";
type ProfitabilityStatus = "profitable" | "unprofitable" | "missing_costs";
type GroupBy = "class" | "category" | "product";

function isClass(row: FinanceSalesRow): row is FinanceSalesClass {
  return "categories" in row;
}

function isCategory(row: FinanceSalesRow): row is FinanceSalesCategory {
  return "products" in row;
}

function summaryValue(
  summary: FinanceSalesCurrencySummary | undefined,
  field: "shipped_sales" | "net_sales" | "cogs" | "gross_profit",
) {
  if (!summary) {
    return "—";
  }
  const value = summary[field];
  const knownValue =
    field === "gross_profit"
      ? summary.known_net_sales - summary.known_cogs
      : summary[`known_${field}` as keyof FinanceSalesCurrencySummary];

  return knownMoney(value, knownValue as number, summary.currency);
}

export default function FinanceSales() {
  const [range, setRange] = useState<FinanceDateRangeValue>(() =>
    financeRangeFromSearch(window.location.search),
  );
  const [page, setPage] = useState(1);
  const [shipmentPage, setShipmentPage] = useState(1);
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortField>("gross_profit");
  const [groupBy, setGroupBy] = useState<GroupBy>("class");
  const [classIds, setClassIds] = useState<number[]>([]);
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [productIds, setProductIds] = useState<number[]>([]);
  const [customerIds, setCustomerIds] = useState<number[]>([]);
  const [currencyIds, setCurrencyIds] = useState<number[]>([]);
  const [profitabilityStatuses, setProfitabilityStatuses] = useState<
    ProfitabilityStatus[]
  >([]);
  const [report, setReport] = useState<FinanceSalesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    fetchFinanceSales(
      range,
      page,
      shipmentPage,
      search,
      sort,
      groupBy,
      classIds,
      categoryIds,
      productIds,
      customerIds,
      currencyIds,
      profitabilityStatuses,
    )
      .then((response) => {
        if (active) {
          setReport(response);
        }
      })
      .catch(() => {
        if (active) {
          setError("Sales and profitability could not be loaded.");
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [
    range.from,
    range.to,
    page,
    shipmentPage,
    search,
    sort,
    groupBy,
    classIds,
    categoryIds,
    productIds,
    customerIds,
    currencyIds,
    profitabilityStatuses,
  ]);

  const primary = report?.summary.by_currency[0];
  const previous = useMemo(
    () =>
      report?.previous_summary.by_currency.find(
        (item) => item.currency_id === primary?.currency_id,
      ),
    [primary?.currency_id, report?.previous_summary.by_currency],
  );
  const previousRange = report?.range
    ? { from: report.range.previous_from, to: report.range.previous_to }
    : null;
  const hasWarnings =
    report !== null &&
    (report.summary.unattributed_credit_items > 0 ||
      report.summary.by_currency.some(
        (item) =>
          item.missing_cost_shipments > 0 || item.missing_price_shipments > 0,
      ));

  const categoryOptions = useMemo(
    () =>
      report?.filters.categories.filter(
        (category) =>
          classIds.length === 0 ||
          (category.class_id !== null && classIds.includes(category.class_id)),
      ) ?? [],
    [classIds, report?.filters.categories],
  );

  const productOptions = useMemo(() => {
    const classCategoryIds =
      classIds.length === 0
        ? null
        : new Set(
            (report?.filters.categories ?? [])
              .filter(
                (category) =>
                  category.class_id !== null &&
                  classIds.includes(category.class_id),
              )
              .map((category) => category.id),
          );

    return (
      report?.filters.products.filter(
        (product) =>
          (classCategoryIds === null ||
            (product.category_id !== null &&
              classCategoryIds.has(product.category_id))) &&
          (categoryIds.length === 0 ||
            (product.category_id !== null &&
              categoryIds.includes(product.category_id))),
      ) ?? []
    );
  }, [categoryIds, classIds, report?.filters.categories, report?.filters.products]);

  const additionalFilterCount =
    Number(customerIds.length > 0) +
    Number(profitabilityStatuses.length > 0) +
    Number(sort !== "gross_profit");
  const hasOptionalFilters =
    search !== "" ||
    groupBy !== "class" ||
    classIds.length > 0 ||
    categoryIds.length > 0 ||
    productIds.length > 0 ||
    customerIds.length > 0 ||
    currencyIds.length > 0 ||
    profitabilityStatuses.length > 0 ||
    sort !== "gross_profit";

  const clearFilters = () => {
    setGroupBy("class");
    setClassIds([]);
    setCategoryIds([]);
    setProductIds([]);
    setCustomerIds([]);
    setCurrencyIds([]);
    setProfitabilityStatuses([]);
    setSort("gross_profit");
    setSearch("");
    setSearchDraft("");
    setPage(1);
    setShipmentPage(1);
  };

  const changeRange = (value: FinanceDateRangeValue) => {
    setRange(value);
    setPage(1);
    setShipmentPage(1);
  };

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    setSearch(searchDraft.trim());
    setPage(1);
    setShipmentPage(1);
  };

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-6 sm:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm font-medium text-emerald-700">Finance</p>
            <h1 className="text-2xl font-semibold text-slate-900">
              Sales &amp; Profitability
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              Shipped sales, material COGS, Gross Profit, credits, and current
              unshipped order-line value.
            </p>
          </div>
          <FinanceDateFilter value={range} onChange={changeRange} />
        </div>

        {hasWarnings && (
          <div className="mt-5">
            <FinanceWarningBanner>
              Some results are incomplete because shipment prices, material
              costs, or credit currencies are missing. Known values are shown
              without treating missing data as $0.
            </FinanceWarningBanner>
          </div>
        )}

        <section className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <FinanceMetricCard
            label={`Shipped Sales${primary ? ` (${primary.currency})` : ""}`}
            value={summaryValue(primary, "shipped_sales")}
            comparison={previousPeriodComparison(
              primary?.shipped_sales ?? null,
              previous?.shipped_sales ?? null,
              previousRange,
            )}
            incomplete={primary?.shipped_sales === null}
          />
          <FinanceMetricCard
            label={`Net Sales${primary ? ` (${primary.currency})` : ""}`}
            value={summaryValue(primary, "net_sales")}
            comparison={previousPeriodComparison(
              primary?.net_sales ?? null,
              previous?.net_sales ?? null,
              previousRange,
            )}
            incomplete={primary?.net_sales === null}
          />
          <FinanceMetricCard
            label={`Material COGS${primary ? ` (${primary.currency})` : ""}`}
            value={summaryValue(primary, "cogs")}
            comparison={previousPeriodComparison(
              primary?.cogs ?? null,
              previous?.cogs ?? null,
              previousRange,
            )}
            incomplete={primary?.cogs === null}
          />
          <FinanceMetricCard
            label={`Gross Profit${primary ? ` (${primary.currency})` : ""}`}
            value={summaryValue(primary, "gross_profit")}
            comparison={previousPeriodComparison(
              primary?.gross_profit ?? null,
              previous?.gross_profit ?? null,
              previousRange,
            )}
            incomplete={primary?.gross_profit === null}
          />
          <FinanceMetricCard
            label="Gross Margin"
            value={formatPercent(primary?.gross_margin)}
            comparison={previousPeriodComparison(
              primary?.gross_margin ?? null,
              previous?.gross_margin ?? null,
              previousRange,
            )}
            incomplete={primary?.gross_margin === null}
          />
          <FinanceMetricCard
            label={`Backlog${primary ? ` (${primary.currency})` : ""}`}
            value={formatMoney(primary?.backlog, primary?.currency)}
          />
        </section>

        {report && report.summary.by_currency.length > 1 && (
          <section className="mt-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="px-4 py-3">
                    <FinanceHelpLabel label="Currency" />
                  </th>
                  <th className="px-4 py-3">
                    <FinanceHelpLabel label="Net Sales" />
                  </th>
                  <th className="px-4 py-3">
                    <FinanceHelpLabel label="COGS" />
                  </th>
                  <th className="px-4 py-3">
                    <FinanceHelpLabel label="Gross Profit" />
                  </th>
                  <th className="px-4 py-3">
                    <FinanceHelpLabel label="Margin" />
                  </th>
                  <th className="px-4 py-3">
                    <FinanceHelpLabel label="Backlog" />
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {report.summary.by_currency.map((item) => (
                  <tr key={item.currency_id}>
                    <td className="px-4 py-3 font-semibold">{item.currency}</td>
                    <td className="px-4 py-3">
                      {formatMoney(item.net_sales, item.currency)}
                    </td>
                    <td className="px-4 py-3">
                      {formatMoney(item.cogs, item.currency)}
                    </td>
                    <td className="px-4 py-3">
                      {formatMoney(item.gross_profit, item.currency)}
                    </td>
                    <td className="px-4 py-3">
                      {formatPercent(item.gross_margin)}
                    </td>
                    <td className="px-4 py-3">
                      {formatMoney(item.backlog, item.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        <section className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <FinanceFilterPanel
            additionalFilterCount={additionalFilterCount}
            hasActiveFilters={hasOptionalFilters}
            onClear={clearFilters}
            primary={
              <>
                <FinanceFilterField label="Grouping">
                  <select
                    value={groupBy}
                    onChange={(event) => {
                      setGroupBy(event.target.value as GroupBy);
                      setPage(1);
    setShipmentPage(1);
                    }}
                  >
                    <option value="class">Class</option>
                    <option value="category">Category</option>
                    <option value="product">Individual Products</option>
                  </select>
                </FinanceFilterField>
                <FinanceFilterField label="Class">
                  <FinanceMultiSelect
                    value={classIds}
                    placeholder="All classes"
                    options={
                      report?.filters.classes.flatMap((item) =>
                        item.id === null
                          ? []
                          : [{ label: item.name, value: item.id }],
                      ) ?? []
                    }
                    onChange={(value) => {
                      setClassIds(value);
                      setCategoryIds([]);
                      setProductIds([]);
                      setPage(1);
    setShipmentPage(1);
                    }}
                  />
                </FinanceFilterField>
                <FinanceFilterField label="Category">
                  <FinanceMultiSelect
                    value={categoryIds}
                    placeholder="All categories"
                    options={categoryOptions.flatMap((item) =>
                      item.id === null
                        ? []
                        : [{ label: item.name, value: item.id }],
                    )}
                    onChange={(value) => {
                      setCategoryIds(value);
                      setProductIds([]);
                      setPage(1);
    setShipmentPage(1);
                    }}
                  />
                </FinanceFilterField>
                <FinanceFilterField label="Product">
                  <FinanceMultiSelect
                    value={productIds}
                    placeholder="All products"
                    options={productOptions.flatMap((item) =>
                      item.id === null
                        ? []
                        : [{ label: item.name, value: item.id }],
                    )}
                    onChange={(value) => {
                      setProductIds(value);
                      setPage(1);
    setShipmentPage(1);
                    }}
                  />
                </FinanceFilterField>
                <FinanceFilterField label="Currency">
                  <FinanceMultiSelect
                    value={currencyIds}
                    placeholder="All currencies"
                    options={
                      report?.filters.currencies.flatMap((item) =>
                        item.id === null
                          ? []
                          : [{ label: item.name, value: item.id }],
                      ) ?? []
                    }
                    onChange={(value) => {
                      setCurrencyIds(value);
                      setPage(1);
    setShipmentPage(1);
                    }}
                  />
                </FinanceFilterField>
              </>
            }
            additional={
              <>
                <FinanceFilterField label="Customer">
                  <FinanceMultiSelect
                    value={customerIds}
                    placeholder="All customers"
                    options={
                      report?.filters.customers.map((item) => ({
                        label: item.name,
                        value: item.id,
                      })) ?? []
                    }
                    onChange={(value) => {
                      setCustomerIds(value);
                      setPage(1);
    setShipmentPage(1);
                    }}
                  />
                </FinanceFilterField>
                <FinanceFilterField label="Profitability status">
                  <FinanceMultiSelect<ProfitabilityStatus>
                    value={profitabilityStatuses}
                    placeholder="All statuses"
                    options={[
                      { label: "Profitable", value: "profitable" },
                      { label: "Unprofitable", value: "unprofitable" },
                      { label: "Missing costs", value: "missing_costs" },
                    ]}
                    onChange={(value) => {
                      setProfitabilityStatuses(value);
                      setPage(1);
    setShipmentPage(1);
                    }}
                  />
                </FinanceFilterField>
                <FinanceFilterField label="Sort by">
                  <select
                    value={sort}
                    onChange={(event) => {
                      setSort(event.target.value as SortField);
                      setPage(1);
    setShipmentPage(1);
                    }}
                  >
                    <option value="gross_profit">Gross Profit</option>
                    <option value="net_sales">Net Sales</option>
                    <option value="gross_margin">Gross Margin</option>
                  </select>
                </FinanceFilterField>
              </>
            }
          />
          <div className="flex flex-col gap-3 border-b border-slate-200 p-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="font-semibold text-slate-900">
                {groupBy === "class"
                  ? "Class profitability"
                  : groupBy === "category"
                    ? "Category profitability"
                    : "Product profitability"}
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Ranked by{" "}
                {sort === "gross_profit"
                  ? "Gross Profit"
                  : sort === "net_sales"
                    ? "Net Sales"
                    : "Gross Margin"}
                .
                {groupBy !== "product" &&
                  " Expand a row to see the next level."}
              </p>
            </div>
            <form className="flex gap-2" onSubmit={submitSearch}>
              <label className="sr-only" htmlFor="sales-search">
                Search product or PLU
              </label>
              <input
                id="sales-search"
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                placeholder="Search class, category, product or PLU"
                value={searchDraft}
                onChange={(event) => setSearchDraft(event.target.value)}
              />
              <button
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                type="submit"
              >
                Search
              </button>
            </form>
          </div>

          {error && (
            <p className="p-5 text-sm font-medium text-red-700">{error}</p>
          )}
          {!error && loading && (
            <div className="p-3">
              <TableLoadingSkeleton rows={7} columns={6} />
            </div>
          )}
          {!error && !loading && report?.data.length === 0 && (
            <p className="p-5 text-sm text-slate-500">
              No shipped products were found for this period.
            </p>
          )}

          {!error && !loading && report && report.data.length > 0 && (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                    <tr>
                      <th className="px-4 py-3">
                        <FinanceHelpLabel
                          label={
                            groupBy === "class"
                              ? "Class"
                              : groupBy === "category"
                                ? "Category"
                                : "Product"
                          }
                        />
                      </th>
                      <th className="px-4 py-3">
                        <FinanceHelpLabel label="Net Sales" />
                      </th>
                      <th className="px-4 py-3">
                        <FinanceHelpLabel label="COGS" />
                      </th>
                      <th className="px-4 py-3">
                        <FinanceHelpLabel label="Gross Profit" />
                      </th>
                      <th className="px-4 py-3">
                        <FinanceHelpLabel label="Margin" />
                      </th>
                      <th className="px-4 py-3">
                        <FinanceHelpLabel
                          label={
                            groupBy === "product"
                              ? "Orders / Shipments"
                              : "Products / Shipments"
                          }
                        />
                      </th>
                      <th className="px-4 py-3">
                        <FinanceHelpLabel label="Status" />
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {report.data.map((row) => {
                      const classRow = isClass(row);
                      const categoryRow = isCategory(row);
                      const rowKey = classRow
                        ? `class-${row.class_id ?? "unclassified"}-${row.currency_id}`
                        : categoryRow
                          ? `category-${row.category_id ?? "uncategorized"}-${row.currency_id}`
                          : `product-${row.product_id}-${row.plu}-${row.currency_id}`;

                      return (
                        <tr key={rowKey}>
                          <td className="px-4 py-4">
                            {classRow ? (
                              <details>
                                <summary className="cursor-pointer font-semibold text-slate-900">
                                  {row.class_name}
                                </summary>
                                <p className="mt-1 text-xs text-slate-500">
                                  {row.categories.length} categories ·{" "}
                                  {row.product_count} products · {row.currency}
                                </p>
                                <div className="mt-3 space-y-3 border-l-2 border-slate-200 pl-3">
                                  {row.categories.map((category) => (
                                    <details
                                      key={`${category.category_id ?? "uncategorized"}-${category.currency_id}`}
                                    >
                                      <summary className="cursor-pointer text-xs font-semibold text-slate-800">
                                        {category.category_name} ·{" "}
                                        {formatMoney(
                                          category.gross_profit,
                                          category.currency,
                                        )}{" "}
                                        Gross Profit
                                      </summary>
                                      <div className="mt-2 space-y-1 pl-3">
                                        {category.products.map((product) => (
                                          <div
                                            className="flex flex-wrap justify-between gap-2 text-xs"
                                            key={`${product.product_id}-${product.plu}-${product.currency_id}`}
                                          >
                                            <span>
                                              {product.product_name ||
                                                product.plu}{" "}
                                              <span className="text-slate-500">
                                                ({product.plu})
                                              </span>
                                            </span>
                                            <span className="font-medium">
                                              {formatMoney(
                                                product.gross_profit,
                                                product.currency,
                                              )}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    </details>
                                  ))}
                                </div>
                              </details>
                            ) : categoryRow ? (
                              <details>
                                <summary className="cursor-pointer font-semibold text-slate-900">
                                  {row.category_name}
                                </summary>
                                <p className="mt-1 text-xs text-slate-500">
                                  {row.class_name} · {row.product_count}{" "}
                                  products · {row.currency}
                                </p>
                                <div className="mt-3 space-y-2 border-l-2 border-slate-200 pl-3">
                                  {row.products.map((product) => (
                                    <div
                                      className="flex flex-wrap items-center justify-between gap-2 text-xs"
                                      key={`${product.product_id}-${product.plu}-${product.currency_id}`}
                                    >
                                      <span>
                                        {product.product_name || product.plu}{" "}
                                        <span className="text-slate-500">
                                          ({product.plu})
                                        </span>
                                      </span>
                                      <span className="font-medium text-slate-700">
                                        {formatMoney(
                                          product.gross_profit,
                                          product.currency,
                                        )}{" "}
                                        Gross Profit
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </details>
                            ) : (
                              <>
                                <p className="font-semibold text-slate-900">
                                  {row.product_name || row.plu}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {row.plu} · {row.currency}
                                  {row.class_name ? ` · ${row.class_name}` : ""}
                                  {row.category_name
                                    ? ` / ${row.category_name}`
                                    : ""}
                                </p>
                              </>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            {formatMoney(row.net_sales, row.currency)}
                          </td>
                          <td className="px-4 py-4">
                            {formatMoney(row.cogs, row.currency)}
                          </td>
                          <td className="px-4 py-4 font-medium">
                            {formatMoney(row.gross_profit, row.currency)}
                          </td>
                          <td className="px-4 py-4">
                            {formatPercent(row.gross_margin)}
                          </td>
                          <td className="px-4 py-4">
                            {"product_count" in row
                              ? `${row.product_count} / ${row.shipment_count}`
                              : `${row.order_count} / ${row.shipment_count}`}
                          </td>
                          <td className="px-4 py-4">
                            {row.complete ? (
                              <span className="inline-flex rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-800">
                                Complete
                              </span>
                            ) : (
                              <FinanceIncompleteBadge
                                count={
                                  row.missing_cost_shipments +
                                  row.missing_price_shipments
                                }
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <FinancePagination
                label={
                  groupBy === "class"
                    ? "classes"
                    : groupBy === "category"
                      ? "categories"
                      : "products"
                }
                page={report.pagination.current_page}
                lastPage={report.pagination.last_page}
                total={report.pagination.total}
                onPageChange={setPage}
              />
            </>
          )}
        </section>

        {report && report.recent_shipments.length > 0 && (
          <section className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 p-4">
              <h2 className="font-semibold text-slate-900">Recent shipments</h2>
              <p className="mt-1 text-sm text-slate-500">
                The newest shipments in the selected period, showing 10 per page.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                  <tr>
                    <th className="px-4 py-3">
                      <FinanceHelpLabel label="Order" />
                    </th>
                    <th className="px-4 py-3">
                      <FinanceHelpLabel label="Product / Lot" />
                    </th>
                    <th className="px-4 py-3">
                      <FinanceHelpLabel label="Quantity" />
                    </th>
                    <th className="px-4 py-3">
                      <FinanceHelpLabel label="Sales" />
                    </th>
                    <th className="px-4 py-3">
                      <FinanceHelpLabel label="COGS" />
                    </th>
                    <th className="px-4 py-3">
                      <FinanceHelpLabel label="Gross Profit" />
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {report.recent_shipments.map((shipment) => (
                    <tr key={shipment.id}>
                      <td className="px-4 py-4">
                        <a
                          className="font-semibold text-emerald-700 hover:underline"
                          href={`/shipments/${shipment.order_id}`}
                        >
                          {shipment.order_number}
                        </a>
                        <p className="text-xs text-slate-500">
                          {shipment.customer_name || "No customer"}
                        </p>
                      </td>
                      <td className="px-4 py-4">
                        <p>{shipment.product_name || shipment.plu}</p>
                        <p className="flex items-center gap-1 text-xs text-slate-500">
                          <span>{shipment.plu} ·</span>
                          <CopyableIdentifier
                            value={shipment.lot_number}
                            description="lot number"
                          />
                        </p>
                      </td>
                      <td className="px-4 py-4">
                        {shipment.quantity.toLocaleString("en-CA")}{" "}
                        {shipment.unit}
                      </td>
                      <td className="px-4 py-4">
                        {formatMoney(shipment.sales, shipment.currency)}
                      </td>
                      <td className="px-4 py-4">
                        {formatMoney(shipment.cogs, shipment.currency)}
                      </td>
                      <td className="px-4 py-4">
                        {formatMoney(shipment.gross_profit, shipment.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <FinancePagination
              label="shipments"
              page={report.recent_shipments_pagination.current_page}
              lastPage={report.recent_shipments_pagination.last_page}
              total={report.recent_shipments_pagination.total}
              onPageChange={setShipmentPage}
            />
          </section>
        )}
      </div>
    </main>
  );
}
